import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MasterDataService } from 'src/app/services/master-data.service';
import { Continent, Country, State, City } from 'src/app/models/master-data.model';
import { forkJoin } from 'rxjs';
import { SpinnerService } from 'src/app/services/shared/spinner.service';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { GeographicalChip } from 'src/app/models/product-model';
import { GeographicalType } from 'src/app/enums/product-enum';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-leads-filter-popup-component',
  templateUrl: './leads-filter-popup-component.component.html',
  styleUrls: ['./leads-filter-popup-component.component.scss']
})
export class LeadsFilterPopupComponentComponent implements OnInit {
  @Output() clearChipsAndFilters = new EventEmitter<any>(); 
  filterForm!: FormGroup;
  countries!: Country[];
  states!: State[];
  cities!: City[];
  searchText: string = '';
  selectedContinents: Continent[] = [];
  selectedCountries: Country[] = [];
  selectedStates: State[] = [];
  selectedCities: City[] = [];
  roleMappingId: any;
  showNoRecordsFounds: boolean = true;
  role: string = '';
  staffData: any[] = [];
  filteredCountries: any[] = [];
  filteredStates: any[] = [];
  filteredCities: any[] = [];
  chips: GeographicalChip[] = [];
  oldChips: GeographicalChip[] = [];
 
  countryControl = new FormControl();
  stateControl = new FormControl();
  cityControl = new FormControl();
  accountTypeControl = new FormControl();
  leadStateControl = new FormControl();
  addedByControl = new FormControl();
  companyNameControl = new FormControl();
  showWarning = false;
  highlightCountry = false;
  highlightState = false;

  filteredStaffData: any[] = []; 
  accountTypes: string[] = []; 
  filteredAccountTypes: string[] = []; 
  leadStates: string[] = []; 
  filteredLeadStates: string[] = []; 
  
  constructor(
    private dialogRef: MatDialogRef<LeadsFilterPopupComponentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { filterData: any, chips: any },
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder, 
    private masterDataService: MasterDataService,
    private localStorageService: LocalStorageService, 
    private endCustomerService: EndCustomerService,
    private spinnerService: SpinnerService    
  ) {}

  async ngOnInit() {   
    this.filterForm = this.fb.group({
      country: [''], 
      state: [''],
      city: [''],
      accountType: [''],
      companyName: [''],
      leadState: [''],
      addedBy: ['']
    });
    this.chips = this.data.chips || [];
    this.oldChips = this.data.chips;
   
    this.loadMasterData();
    this.getAllStaff();
    this.loadStates(['']);
    this.loadCities(['']);     
     
    this.countryControl.valueChanges.subscribe(value => {
      if (!value?.countryName) {
        if (this.countries) {
          this.filteredCountries = this.countries.filter(country =>
            country.countryName.toLowerCase().includes(value?.toLowerCase() || '')
          );
        }
      }
    });
   
    this.stateControl.valueChanges.subscribe(value => {
      if (!value?.stateName) {
        if (this.states) {
          const searchTerm = this.normalizeText(value?.toLowerCase() || '');
          this.filteredStates = this.states.filter(state =>
            this.normalizeText(state.stateName.toLowerCase()).includes(searchTerm)
          );
        }else {
          this.filteredStates = []; 
        }
      }
    }); 
   
    this.cityControl.valueChanges.subscribe(value => {
      if (!value?.cityName) {
        if (this.cities) {
          const searchTerm = this.normalizeText(value?.toLowerCase() || '');
          this.filteredCities = this.cities.filter(city =>
            this.normalizeText(city.cityName.toLowerCase()).includes(searchTerm)
          );
        }else {
          this.filteredCities = []; 
        }
      }
    }); 
   
    this.accountTypeControl.valueChanges.subscribe(value => {
      if (typeof value === 'string') {
        const searchTerm = value.toLowerCase();
        this.filteredAccountTypes = this.accountTypes.filter(type => 
          type.toLowerCase().includes(searchTerm)
        );
      }
      
      if (value) {
        this.handleAccountTypeChange(value);
      }
    });
   
    
    this.leadStateControl.valueChanges.subscribe(value => {
      if (typeof value === 'string') {
        const searchTerm = value.toLowerCase();
        this.filteredLeadStates = this.leadStates.filter(state => 
          state.toLowerCase().includes(searchTerm)
        );
      }
      
      if (value) {
        this.handleLeadStateChange(value);
      }
    });
   
   
    this.addedByControl.valueChanges.subscribe(value => {
      if (!value?.user_name) {
        const searchTerm = typeof value === 'string' ? value.toLowerCase() : '';
        this.filteredStaffData = this.staffData.filter(staff => 
          staff.user_name.toLowerCase().includes(searchTerm)
        );
      }
      
      if (value && typeof value === 'object') {
        this.handleAddedByChange(value.user_name);
      }
    });
   
    
    this.companyNameControl.valueChanges.subscribe(value => {
      if (value) {
        this.handleCompanyNameChange(value);
      }
    });
    this.filterForm.get('accountType')?.valueChanges.subscribe(accountType => {
      if (accountType) {
        this.handleAccountTypeChange(accountType);
      }
    });
   
    this.filterForm.get('leadState')?.valueChanges.subscribe(leadState => {
      if (leadState) {
        this.handleLeadStateChange(leadState);
      }
    });
   
    this.filterForm.get('addedBy')?.valueChanges.subscribe(addedBy => {
      if (addedBy) {
        this.handleAddedByChange(addedBy);
      }
    });
   
    this.filterForm.get('companyName')?.valueChanges.subscribe(companyName => {
      if (companyName) {
        this.handleCompanyNameChange(companyName);
      }
    });
    
    this.filterForm.valueChanges.subscribe(() => {
      if (this.filterForm.get('country')?.value === '' && 
          this.filterForm.get('state')?.value === '' && 
          this.filterForm.get('city')?.value === '') {
        this.resetValidationHighlights();
      }
    });
  }

  private normalizeText(text: string): string {
    if (!text) return '';
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  resetValidationHighlights() {
    this.highlightCountry = false;
    this.highlightState = false;
    this.showWarning = false;
  }

  submitFilter() { 
    const payload = {
      countries: this.selectedCountries?.map(c => c.countryName) || [] ,
      states: this.selectedStates?.map(c => c.stateName) || [] ,
      cities: this.selectedCities?.map(c => c.cityName) || [] ,
      accountType: this.filterForm.value.accountType || '',
      companyName: this.filterForm.value.companyName || '',
      leadState: this.filterForm.value.leadState || '',
      addedBy: this.filterForm.value.addedBy || '',      
    };
    const isAllFiltersEmpty = Object.entries(payload).every(([_, val]) => {
          if (Array.isArray(val)) return val.length === 0;
          if (val === null || val === undefined) return true;
          if (typeof val === 'string') return val.trim().length === 0;
          return false;
        });
        
        if (isAllFiltersEmpty) {
          Swal.fire({
            icon: 'warning',
            title: 'Missing Filters',
            text: 'Please select at least one filter!',
            confirmButtonText: 'OK',
          });
          return;
        }
    this.dialogRef.close(payload);
  }

  clearFilter() {
    this.chips = [];
    this.filterForm.reset();    
    this.selectedCountries = [];
    this.selectedStates = [];
    this.selectedCities = [];
    this.loadMasterData();
    this.resetValidationHighlights();  
    this.clearChipsAndFilters.emit({
      chips: [],
      filters: {
        country: '',
        state: '',
        city: '',
        accountType: '',
        companyName: '',
        leadState: '',
        addedBy: ''
      }
    });
    this.filteredAccountTypes = [...this.accountTypes];
    this.filteredLeadStates = [...this.leadStates];
    this.filteredStaffData = [...this.staffData];    
  }
  cancelFilter(){   
    this.dialogRef.close(null);
  }

  private loadMasterData() {
    this.spinnerService.showSpinner();
    forkJoin({
      countries: this.masterDataService.getCountries(0, 250, '', []),
    }).subscribe(
      (results) => {
        this.countries = results.countries.data || []; 
        this.filteredCountries = [...this.countries]; 
        this.spinnerService.hideSpinner();
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error fetching master data:', error);
        this.spinnerService.hideSpinner();
        this.cdr.detectChanges();
      }
    );
  }

  getAllStaff(){
    this.role=this.localStorageService.getItem('roleName');
    if(this.role=='OEM'){
      this.roleMappingId=this.localStorageService.getItem('oemId');
    } else if(this.role=='DISTRIBUTOR'){
      this.roleMappingId=this.localStorageService.getItem('distributorId');
    }
    this.showNoRecordsFounds=false;   
    this.endCustomerService.getAllStaffs(this.role, this.roleMappingId, 100, 0)
  .subscribe((data: any) => {
    if (data && data.data && data.data.length > 0) {
      this.staffData = data.data;
      this.filteredStaffData = [...this.staffData];
    } else {
      this.staffData = [];
      this.showNoRecordsFounds = true;
    }
  });
  } 

  loadStates(countryNames: string[]): Promise<void> {
    if(!this.selectedCountries || this.selectedCountries.length === 0){
      this.states = [];
      this.cities = [];
      this.filteredStates = [];
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.masterDataService.getStates(0, 500, '', countryNames).subscribe({
        next: (data) => {
          this.states = data.data;
          this.filteredStates = this.selectedCountries.length > 0 ? [...this.states] : [];
          resolve();
        },
        error: (error) => {        
          reject(error);
        }
      });
    });
  }

  loadCities(stateNames: string[]): Promise<void> {
    if(!this.selectedStates || this.selectedStates.length === 0){
      this.cities = [];
      this.filteredCities = [];
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.masterDataService.getCities(0, 500, '', stateNames).subscribe({
        next: (data) => {
          this.cities = data.data;
          this.filteredCities = this.selectedStates.length > 0 ? [...this.cities] : [];
          resolve();
        },
        error: (error) => {         
          reject(error);
        }
      });
    });
  }

  onCountryChange(country: Country) {   
    if (!this.selectedCountries?.some(c => c.id === country.id)) {
      this.selectedCountries?.push(country);
      this.addNewChip(this.createChipStructure(country.countryName, GeographicalType.COUNTRY, 2, country.id));
    }  
    const countryNames = this.selectedCountries?.map(c => c.countryName);
    this.loadStates(countryNames);
    this.highlightCountry = false;
    this.showWarning = false;
    this.highlightState = false;
    this.filterForm.get('country')?.reset();
  }

  onStateChange(state: State) {
    if (!this.selectedStates?.some(c => c.id === state.id)) {
      this.selectedStates?.push(state);
      this.addNewChip(this.createChipStructure(state.stateName, GeographicalType.STATE, 3, state.id));
      this.stateControl.reset();
    }
    const stateNames = this.selectedStates?.map(c => c.stateName);
    this.loadCities(stateNames);
    this.highlightState = false;
    this.showWarning = false;
    this.filterForm.get('state')?.reset();
  }

  onCityChange(city: City) {
    if (!this.selectedCities?.some(c => c.id === city.id)) {
      this.selectedCities?.push(city);
      this.addNewChip(this.createChipStructure(city.cityName, GeographicalType.CITY, 4, city.id));
    }    
    this.filterForm.get('city')?.reset();
  }

  handleAccountTypeChange(accountType: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.ACCOUNT);
    const accountTypeChip: GeographicalChip = {
      id: 0,
      name: accountType,
      type: GeographicalType.ACCOUNT,
      priority: 10
    };
    this.addNewChip(accountTypeChip);
  }
 
  handleLeadStateChange(leadState: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.LEAD_STATE);
    const leadStateChip: GeographicalChip = {
      id: 0,
      name: leadState,
      type: GeographicalType.LEAD_STATE,
      priority: 13
    };
    this.addNewChip(leadStateChip);
  }
 
  handleAddedByChange(addedBy: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.ADDED_BY);
    const addedByChip: GeographicalChip = {
      id: 0,
      name: addedBy,
      type: GeographicalType.ADDED_BY,
      priority: 14
    };
    this.addNewChip(addedByChip);
  }
 
  handleCompanyNameChange(companyName: string) {
    this.chips = this.chips.filter(chip => chip.type !== GeographicalType.COMPANY_NAME);
    const companyNameChip: GeographicalChip = {
      id: 0,
      name: companyName,
      type: GeographicalType.COMPANY_NAME,
      priority: 15
    };
    this.addNewChip(companyNameChip);
  }
 
  receiveSearchText(searchText: string): void {
    this.searchText = searchText;   
  }

  removeContinentForCountry(country: Country) {
    const continentCode = country.continentCode;   
    this.selectedContinents = this.selectedContinents?.filter(continent => continent.continentCode !== continentCode);
  }

  removeCountryForState(state: State) {
    const countryName = state.countryName;    
    const country = this.selectedCountries?.find(c => c.countryName === countryName);
    if (country) {
      this.removeContinentForCountry(country);      
      this.selectedCountries = this.selectedCountries?.filter(country => country.countryName !== countryName);
    }
  }
  
  onOptionChange(type: string) {
    let searchTerm = this.filterForm.get(type)?.value;
    
    if (searchTerm && typeof searchTerm === 'object') {
      return;
    }
    
    searchTerm = searchTerm ? searchTerm.toLowerCase() : '';
    
    switch(type) {
      case 'country':
        if (this.countries && this.countries.length > 0) {
          this.filteredCountries = this.countries.filter(country =>
            country.countryName.toLowerCase().includes(searchTerm)
          );
        } 
        break;
      case 'state':
        if (this.states && this.states.length > 0) {
          this.filteredStates = this.states.filter(state =>
          this.normalizeText(state.stateName.toLowerCase()).includes(searchTerm)
          );
        } 
        break;
      case 'city':
        if (this.cities && this.cities.length > 0) {
          this.filteredCities = this.cities.filter(city =>
            this.normalizeText(city.cityName.toLowerCase()).includes(searchTerm)
          );
        } 
        break;      
    }
    
    if (searchTerm === '') {
      if (type === 'country') {
        this.highlightCountry = false;
      } else if (type === 'state') {
        this.highlightState = false;
      }
      
      if ((!this.filterForm.get('country')?.value || this.filterForm.get('country')?.value === '') && 
          (!this.filterForm.get('state')?.value || this.filterForm.get('state')?.value === '') && 
          (!this.filterForm.get('city')?.value || this.filterForm.get('city')?.value === '')) {
        this.showWarning = false;
      }
    }
  }

  removeStateForCity(city: City) {
    const stateName = city.stateName;    
    const state = this.selectedStates?.find(c => c.stateName === stateName);
    if (state) {
      this.removeCountryForState(state);     
      this.selectedStates = this.selectedStates?.filter(state => state.stateName !== stateName);
    }
  }
  
  displayCountry(country: any): string {
    return country ? country.countryName : '';
  }
   
  displayState(state: any): string {
    return state ? state.stateName : '';
  }
   
  displayCity(city: any): string {
    return city ? city.cityName : '';
  }  

  remove(removedChip: GeographicalChip) {
    const index = this.chips.indexOf(removedChip);
    if (index >= 0) {
      this.chips = this.chips.filter((chip, id) => id !== index);
     
      switch (removedChip.type) {
        case GeographicalType.COUNTRY:
          this.selectedCountries = this.selectedCountries.filter(country => country.countryName !== removedChip.name);
          this.filterForm.patchValue({ country: this.selectedCountries[this.selectedCountries.length - 1] || '' });
          this.loadStates(this.selectedCountries.map(c => c.countryName));
          
          this.highlightCountry = false;
          
          if (this.selectedCountries.length === 0) {
            this.highlightState = false;
          }
          break;
         
        case GeographicalType.STATE:
          this.selectedStates = this.selectedStates.filter(state => state.stateName !== removedChip.name);
          this.filterForm.patchValue({ state: this.selectedStates[this.selectedStates.length - 1] || '' });
          this.loadCities(this.selectedStates.map(c => c.stateName));
          
          this.highlightState = false;
          break;
         
        case GeographicalType.CITY:
          this.selectedCities = this.selectedCities.filter(city => city.cityName !== removedChip.name);
          this.filterForm.patchValue({ city: this.selectedCities[this.selectedCities.length - 1] || '' });
          break;
         
        case GeographicalType.ACCOUNT:
          this.filterForm.patchValue({ accountType: '' });
          break;
         
        case GeographicalType.COMPANY_NAME:
          this.filterForm.patchValue({ companyName: '' });
          break;
         
        case GeographicalType.LEAD_STATE:
          this.filterForm.patchValue({ leadState: '' });
          break;
         
        case GeographicalType.ADDED_BY:
          this.filterForm.patchValue({ addedBy: '' });
          break;          
        default:
          break;
      }
      
      if (this.selectedCountries.length === 0 && this.selectedStates.length === 0 && this.selectedCities.length === 0) {
        this.showWarning = false;
      }
    }
  }
  displayAddedBy(staff: any): string {
    return staff ? staff.user_name : '';
  }
  
  onAddedBySearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredStaffData = this.staffData.filter(staff => 
      staff.user_name.toLowerCase().includes(searchTerm)
    );
  }
  addNewChip(newChip: GeographicalChip) {
    const chipExists = this.chips.some(chip =>
      chip.name === newChip.name && chip.type === newChip.type
    );  
    if (!chipExists) {
      this.chips = [...this.chips, newChip];
    }
  }
 
  createChipStructure(name: string, type: GeographicalType, priority: number, id: number = 0): GeographicalChip {
    return {
      id: id,
      name: name,
      type: type,
      priority: priority
    };
  }

  onStateFocus(): void {
    const country = this.selectedCountries;  
    const isCountrySelected = country && country.length > 0;   
    this.highlightCountry = !isCountrySelected;   
    if (!isCountrySelected) {
      this.showWarning = true;       
    } else {
      this.showWarning = false;
    }
  }   
 
  onCityFocus(): void {
    const country = this.selectedCountries;
    const state = this.selectedStates;
 
    const isCountrySelected = country && country.length > 0;
    const isStateSelected = state && state.length > 0;
 
    this.highlightCountry = !isCountrySelected;
    this.highlightState = !isStateSelected;
 
    if (!isCountrySelected || !isStateSelected) {
      this.showWarning = true;  
    } else {
      this.showWarning = false;
    }
  }
}